/**
 * PrimaryFocusContext
 * -------------------
 * Declarative signal: "this surface is showing a Primary Focus card right now".
 *
 * Why it exists
 *   The OnboardingStrip is a global ambient signal rendered inside Navigation.
 *   On surfaces that already feature a large Primary Focus card (today: the
 *   Vendor Dashboard "welcome" tab), the strip duplicates the message. This
 *   context lets any such surface declare itself as the Primary Focus owner;
 *   the strip reads the flag and hides itself there.
 *
 * Extensibility
 *   The mechanism is intentionally generic. It is NOT tied to payouts, to
 *   onboarding, or to any specific route/tab. Any future surface that wants
 *   to be the singular Primary Focus host just renders <DeclarePrimaryFocus />
 *   while it is mounted. The strip suppresses itself automatically.
 *
 * Reentrancy
 *   The provider counts active claimants, so multiple surfaces (or strict-mode
 *   double-mounts) cannot accidentally cancel each other.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const PrimaryFocusContext = createContext({
  hasPrimaryFocus: false,
  acquire: () => () => {},
});

export const PrimaryFocusProvider = ({ children }) => {
  const [count, setCount] = useState(0);

  const acquire = useCallback(() => {
    setCount((c) => c + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setCount((c) => Math.max(0, c - 1));
    };
  }, []);

  return (
    <PrimaryFocusContext.Provider value={{ hasPrimaryFocus: count > 0, acquire }}>
      {children}
    </PrimaryFocusContext.Provider>
  );
};

export const usePrimaryFocus = () => useContext(PrimaryFocusContext);

/**
 * Drop-in marker — render this inside any surface that owns the Primary
 * Focus card while that surface is mounted. Renders nothing.
 */
export const DeclarePrimaryFocus = () => {
  const { acquire } = usePrimaryFocus();
  const releaseRef = useRef(null);

  useEffect(() => {
    releaseRef.current = acquire();
    return () => {
      if (releaseRef.current) releaseRef.current();
    };
  }, [acquire]);

  return null;
};

export default PrimaryFocusContext;
